import { RoleGate } from "@/common/components/layout/RoleGate";
import { Baby, ClipboardList, Droplet, MapPin, Shuffle, Sparkles, XCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { AnimalDetail, AnimalRetiredReason } from "../types";
import {
  canRecordCalving,
  canRecordDryOff,
  canRecordFirstCalving,
  canTransitionStage,
} from "../utils/eventPreconditions";
import { formatDate } from "../utils/formatDate";
import { ChangeLotDialog } from "./ChangeLotDialog";
import { ChangeStageDialog } from "./ChangeStageDialog";
import { RecordCalvingDialog } from "./RecordCalvingDialog";
import { RecordDryOffDialog } from "./RecordDryOffDialog";
import { RecordFirstCalvingDialog } from "./RecordFirstCalvingDialog";
import { RegisterTerminationDialog } from "./RegisterTerminationDialog";

const RETIRED_REASON_LABEL: Record<AnimalRetiredReason, string> = {
  died: "Muerte",
  culled: "Descarte",
  sold: "Venta",
};

type Props = {
  accountId: string;
  animal: AnimalDetail;
};

export function AnimalActionsSidebar({ accountId, animal }: Props) {
  const [changeLotOpen, setChangeLotOpen] = useState(false);
  const [firstCalvingOpen, setFirstCalvingOpen] = useState(false);
  const [calvingOpen, setCalvingOpen] = useState(false);
  const [dryOffOpen, setDryOffOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [terminationOpen, setTerminationOpen] = useState(false);

  const isRetired = Boolean(animal.retiredAt);
  // B77 (decisión 2026-06-12): un animal descartado sigue en inventario
  // (1.4.1) — admite registrar su venta o muerte como evento final.
  const isCulledForSale = animal.lifecycleState === "culled_for_sale";
  const canRegisterTermination = !isRetired || isCulledForSale;
  const canChangeLot = !isRetired && Boolean(animal.currentLotId);
  const showFirstCalving = canRecordFirstCalving(animal);
  const showCalving = canRecordCalving(animal);
  const showDryOff = canRecordDryOff(animal);
  const showStage = canTransitionStage(animal);

  return (
    <section
      aria-label="Acciones"
      className="rounded-2xl border border-app-border bg-app-surface p-4 md:p-5"
    >
      <h2 className="mb-3 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        Acciones
      </h2>
      <ul className="space-y-2">
        {/* B107: "Cambiar de lote" gateado a roles que mutan (espejo de los demás
            CTAs); un viewer no debe ver un botón muerto (el backend ya frena con 403). */}
        <RoleGate roles={["owner", "admin", "operator"]}>
          <li>
            <button
              type="button"
              onClick={() => setChangeLotOpen(true)}
              disabled={!canChangeLot}
              className="flex w-full items-center gap-3 rounded-xl border border-app-border bg-app-surface-2 px-3 py-2.5 text-left transition-colors hover:border-app-ink-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MapPin className="h-4 w-4 shrink-0 text-app-ink-3" aria-hidden />
              <span className="flex flex-1 flex-col">
                <span className="font-medium text-[13px] text-app-ink-2">Cambiar de lote</span>
                {!canChangeLot && (
                  <span className="text-[11px] text-app-ink-3">
                    {isRetired ? "Animal retirado" : "Sin lote actual"}
                  </span>
                )}
              </span>
            </button>
          </li>
        </RoleGate>

        <RoleGate roles={["owner", "admin", "operator"]}>
          {showFirstCalving && (
            <li>
              <ActionButton
                label="Registrar primer parto"
                subLabel="Frontera contable"
                icon={Sparkles}
                onClick={() => setFirstCalvingOpen(true)}
              />
            </li>
          )}
          {showCalving && (
            <li>
              <ActionButton
                label="Registrar parto"
                icon={Baby}
                onClick={() => setCalvingOpen(true)}
              />
            </li>
          )}
          {showDryOff && (
            <li>
              <ActionButton
                label="Registrar secado"
                icon={Droplet}
                onClick={() => setDryOffOpen(true)}
              />
            </li>
          )}
          {showStage && (
            <li>
              <ActionButton
                label="Cambiar etapa"
                icon={Shuffle}
                onClick={() => setStageOpen(true)}
              />
            </li>
          )}
        </RoleGate>

        <li>
          <Link
            to={`/app/hato/${animal.id}/timeline`}
            className="flex w-full items-center gap-3 rounded-xl border border-app-border bg-app-surface-2 px-3 py-2.5 text-left transition-colors hover:border-app-ink-3"
          >
            <ClipboardList className="h-4 w-4 shrink-0 text-app-ink-3" aria-hidden />
            <span className="font-medium text-[13px] text-app-ink-2">Ver línea de tiempo</span>
          </Link>
        </li>

        {canRegisterTermination && (
          <RoleGate roles={["owner", "admin", "operator"]}>
            <li>
              <ActionButton
                label={isCulledForSale ? "Registrar venta o muerte" : "Registrar terminación"}
                subLabel={
                  isCulledForSale ? "El animal está descartado, en espera de venta" : undefined
                }
                icon={XCircle}
                onClick={() => setTerminationOpen(true)}
              />
            </li>
          </RoleGate>
        )}
      </ul>

      {isRetired && animal.retiredAt && (
        <p
          aria-label="Animal dado de baja"
          className="mt-3 rounded-xl border border-app-border bg-app-surface-2 px-3 py-2.5 text-[13px] text-app-ink-2"
        >
          {isCulledForSale
            ? `Animal descartado el ${formatDate(animal.retiredAt)} · En espera de venta`
            : `Animal dado de baja el ${formatDate(animal.retiredAt)}${
                animal.retiredReason ? ` · ${RETIRED_REASON_LABEL[animal.retiredReason]}` : ""
              }`}
        </p>
      )}

      <ChangeLotDialog
        accountId={accountId}
        animalId={animal.id}
        currentLotId={animal.currentLotId}
        open={changeLotOpen}
        onOpenChange={setChangeLotOpen}
      />
      {showFirstCalving && (
        <RecordFirstCalvingDialog
          accountId={accountId}
          animal={animal}
          open={firstCalvingOpen}
          onOpenChange={setFirstCalvingOpen}
        />
      )}
      {showCalving && (
        <RecordCalvingDialog
          accountId={accountId}
          animal={animal}
          open={calvingOpen}
          onOpenChange={setCalvingOpen}
        />
      )}
      {showDryOff && (
        <RecordDryOffDialog
          accountId={accountId}
          animal={animal}
          open={dryOffOpen}
          onOpenChange={setDryOffOpen}
        />
      )}
      {showStage && (
        <ChangeStageDialog
          accountId={accountId}
          animal={animal}
          open={stageOpen}
          onOpenChange={setStageOpen}
        />
      )}
      {canRegisterTermination && (
        <RoleGate roles={["owner", "admin", "operator"]}>
          <RegisterTerminationDialog
            accountId={accountId}
            animal={animal}
            open={terminationOpen}
            onOpenChange={setTerminationOpen}
          />
        </RoleGate>
      )}
    </section>
  );
}

type IconType = typeof MapPin;

function ActionButton({
  label,
  subLabel,
  icon: Icon,
  onClick,
}: {
  label: string;
  subLabel?: string;
  icon: IconType;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-app-border bg-app-surface-2 px-3 py-2.5 text-left transition-colors hover:border-app-ink-3"
    >
      <Icon className="h-4 w-4 shrink-0 text-app-ink-3" aria-hidden />
      <span className="flex flex-1 flex-col">
        <span className="font-medium text-[13px] text-app-ink-2">{label}</span>
        {subLabel && <span className="text-[11px] text-app-ink-3">{subLabel}</span>}
      </span>
    </button>
  );
}
